import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarCheck2, CircleAlert, Link2, Pencil, Plus, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import {
  WEEKDAYS,
  bookingLink,
  dateShort,
  fieldErrors,
  minutesToTime,
  srcTag,
  timeToMinutes,
  timeZoneOptions,
  useAvailability,
  useBookSettings,
  useCalendar,
  useEventTypes,
  type EventType,
  type Override,
  type Question,
  type WeeklyRule,
} from "../../lib/bookings";
import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  CardList,
  CardRow,
  CardSection,
  Checkbox,
  CopyField,
  Field,
  FieldError,
  IconButton,
  Input,
  ListEditor,
  Modal,
  PageHeader,
  SelectField,
  Skeleton,
  Textarea,
  notifyError,
  toaster,
  useConfirm,
} from "../../components/ui";
import { ErrorSummary, type FieldIssue } from "../../components/step-editor";
import { usePageTitle } from "./components";
import { BookError, BookGate, invalidateBook } from "./book-components";

/**
 * Scheduling: everything about book.metrickle.com that isn't a booking. The calendar connection,
 * booking links (tagged for a meetup), call types, weekly hours, date overrides and your details.
 * All of it lives in the booking app; this page edits it through /admin/book/*.
 */
export function AdminScheduling() {
  usePageTitle("Scheduling");
  return (
    <>
      <PageHeader title="Scheduling" subtitle="Your calendar, call types, hours and booking links for book.metrickle.com." />
      <BookGate>
        <div className="grid gap-4 @4xl:grid-cols-2">
          <div className="min-w-0 space-y-4">
            <CalendarCard />
            <EventTypesCard />
            <SettingsCard />
          </div>
          <div className="min-w-0 space-y-4">
            <LinksCard />
            <HoursCard />
            <OverridesCard />
          </div>
        </div>
      </BookGate>
    </>
  );
}

// --- Calendar ------------------------------------------------------------------------------------

function CalendarCard() {
  const q = useCalendar();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [connecting, setConnecting] = useState(false);
  const [ids, setIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const c = q.data;
  useEffect(() => {
    if (c?.connected) setIds(c.calendarIds);
  }, [c]);

  const connect = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      const { url } = await api<{ url: string }>("/admin/book/oauth/google/start-link", { method: "POST" });
      window.location.assign(url);
    } catch (err) {
      notifyError(err);
      setConnecting(false);
    }
  };
  const saveIds = async () => {
    const clean = ids.map((i) => i.trim()).filter(Boolean);
    if (!clean.length) {
      setError("Keep at least one calendar");
      return;
    }
    setError(undefined);
    setSaving(true);
    try {
      await api("/admin/book/calendar/calendars", { method: "PUT", json: { calendarIds: clean } });
      toaster.success({ title: "Busy calendars saved" });
      await invalidateBook(qc);
    } catch (err) {
      const f = fieldErrors(err);
      if (f.calendarIds) setError(f.calendarIds);
      else notifyError(err);
    } finally {
      setSaving(false);
    }
  };
  const disconnect = async () => {
    if (!(await confirm({ title: "Disconnect Google Calendar?", description: "Booking pauses until you connect a calendar again. Upcoming calls stay booked.", confirmLabel: "Disconnect" }))) return;
    try {
      await api("/admin/book/calendar/disconnect", { method: "POST" });
      toaster.success({ title: "Google Calendar disconnected" });
      await invalidateBook(qc);
    } catch (err) {
      notifyError(err);
    }
  };

  const dirty = c?.connected && JSON.stringify(ids) !== JSON.stringify(c.calendarIds);
  return (
    <Card>
      <CardHeader
        title="Calendar"
        subtitle="Times are free only when your Google Calendar is. Each booking adds a Meet link."
        actions={
          c && c.mode === "google" ? (
            <Button size="sm" variant={c.connected ? "secondary" : "primary"} onClick={connect} pending={connecting} pendingLabel="Opening Google…" disabled={!c.googleConfigured}>
              {c.connected ? "Reconnect" : "Connect Google Calendar"}
            </Button>
          ) : undefined
        }
      />
      {q.isError ? (
        <BookError error={q.error} title="Couldn't load the calendar" />
      ) : !c ? (
        <Skeleton className="m-4 h-20" />
      ) : (
        <>
          <CardSection className="space-y-3">
            {c.mode === "none" && <Callout tone="info">This environment doesn't read a calendar, so every time in your hours counts as free.</Callout>}
            {c.mode === "google" && !c.googleConfigured && (
              <Callout tone="warn" title="Google isn't set up on the booking app">
                Set GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET and TOKEN_KEK there first (its README, “Connecting Google Calendar”).
              </Callout>
            )}
            {c.connected ? (
              <p className="flex flex-wrap items-center gap-2 text-sm text-ink" role="status">
                {c.status === "ok" ? (
                  <>
                    <CalendarCheck2 size={15} className="text-good" aria-hidden /> Connected as <span className="font-medium">{c.email}</span>
                  </>
                ) : (
                  <>
                    <CircleAlert size={15} className="text-bad" aria-hidden /> <span className="font-medium">{c.status === "failing" ? "Failing" : "Disconnected"}</span>
                    {c.failingSince && <span className="text-ink-2">since {dateShort(c.failingSince, Intl.DateTimeFormat().resolvedOptions().timeZone)}</span>}
                    <span className="text-ink-2">· {c.email}. Booking is paused until it works again.</span>
                  </>
                )}
              </p>
            ) : (
              c.mode === "google" && <p className="text-sm text-ink-2">Not connected. Booking is paused until you connect a calendar.</p>
            )}
            {c.connected && c.lastError && c.status !== "ok" && <p className="text-xs text-ink-3">Google said: {c.lastError}</p>}
          </CardSection>
          {c.connected && (
            <CardSection className="space-y-2">
              <fieldset id="busy-calendars" tabIndex={-1} className="space-y-2 outline-none">
                <legend className="text-[13px] font-medium text-ink">Calendars that count as busy</legend>
                <p className="text-xs text-ink-3">“primary” is your main calendar. Add others by their calendar id from Google Calendar's settings.</p>
                <ListEditor
                  items={ids}
                  onChange={setIds}
                  create={() => ""}
                  noun="calendar"
                  label="Calendars that count as busy"
                  keyOf={(_, i) => i}
                  reorder={false}
                  min={1}
                  max={10}
                  rowClassName="flex items-center gap-2"
                  render={(id, i, controls) => (
                    <>
                      <Input aria-label={`Calendar ${i + 1}`} value={id} onChange={(e) => setIds(ids.map((x, j) => (j === i ? e.target.value : x)))} className="flex-1" aria-invalid={error ? true : undefined} />
                      {controls}
                    </>
                  )}
                />
                {error && <FieldError role="alert">{error}</FieldError>}
              </fieldset>
              <div className="flex justify-between gap-2">
                <Button size="sm" variant="ghost" onClick={disconnect}>
                  Disconnect
                </Button>
                <Button size="sm" variant="primary" onClick={saveIds} disabled={!dirty} pending={saving} pendingLabel="Saving…">
                  Save calendars
                </Button>
              </div>
            </CardSection>
          )}
        </>
      )}
      {dialog}
    </Card>
  );
}

// --- Booking links -------------------------------------------------------------------------------

function LinksCard() {
  const q = useEventTypes();
  const [tag, setTag] = useState("");
  const [typeId, setTypeId] = useState<string>("");
  const listed = (q.data?.eventTypes ?? []).filter((t) => t.active);
  const chosen = listed.find((t) => t.id === typeId) ?? listed[0];
  return (
    <Card>
      <CardHeader title="Booking links" subtitle="Share these. A tagged link shows where someone came from on their booking and in People." />
      {q.isError ? (
        <BookError error={q.error} title="Couldn't load call types" />
      ) : !q.data ? (
        <Skeleton className="m-4 h-24" />
      ) : !listed.length ? (
        <CardSection>
          <p className="text-sm text-ink-3">Add an active call type to get a link.</p>
        </CardSection>
      ) : (
        <>
          <CardSection className="space-y-3">
            <CopyField label="All call types" value={q.data.publicOrigin} done="Link copied" />
            {listed.map((t) => (
              <CopyField key={t.id} label={t.title} value={bookingLink(q.data.publicOrigin, t.slug)} hint={t.listed ? undefined : "Not listed on the booking page: only people with this link can book it."} done="Link copied" />
            ))}
          </CardSection>
          <CardSection className="space-y-3">
            <p className="flex items-center gap-1.5 text-[13px] font-medium text-ink">
              <Link2 size={14} aria-hidden /> Tag a link for a meetup or a signature
            </p>
            <div className="grid gap-3 @xl:grid-cols-2">
              <Field label="Where it's for" hint="Becomes the tag, like brighton-accessibility-meetup-october.">
                <Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Brighton accessibility meetup, October" maxLength={80} />
              </Field>
              <Field label="Call type">
                <SelectField className="w-full" items={listed.map((t) => ({ value: t.id, label: t.title }))} value={chosen?.id} onChange={setTypeId} />
              </Field>
            </div>
            {chosen && srcTag(tag) ? (
              <CopyField label="Tagged link" value={bookingLink(q.data.publicOrigin, chosen.slug, tag)} done="Tagged link copied" />
            ) : (
              <p className="text-xs text-ink-3">Type where it's for to make the link.</p>
            )}
          </CardSection>
        </>
      )}
    </Card>
  );
}

// --- Call types ----------------------------------------------------------------------------------

function EventTypesCard() {
  const q = useEventTypes();
  const [editing, setEditing] = useState<EventType | "new" | null>(null);
  return (
    <Card>
      <CardHeader
        title="Call types"
        subtitle="What people can book, how long, and when."
        actions={
          <Button size="sm" onClick={() => setEditing("new")}>
            <Plus size={14} aria-hidden /> New call type
          </Button>
        }
      />
      {q.isError ? (
        <BookError error={q.error} title="Couldn't load call types" />
      ) : (
        <CardList loading={!q.data} empty="No call types yet.">
          {q.data?.eventTypes.map((t) => (
            <CardRow key={t.id} wrap>
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-ink">{t.title}</span>
                  {!t.active ? <Badge>Off</Badge> : !t.listed && <Badge>Link only</Badge>}
                </span>
                <span className="block text-xs text-ink-3">
                  {t.durationMin} min · up to {t.dailyCap} a day · {t.minNoticeMin >= 60 ? `${Math.round(t.minNoticeMin / 60)} h` : `${t.minNoticeMin} min`} notice · /{t.slug}
                </span>
              </span>
              <Button size="sm" variant="ghost" onClick={() => setEditing(t)} aria-label={`Edit ${t.title}`}>
                <Pencil size={13} aria-hidden /> Edit
              </Button>
            </CardRow>
          ))}
        </CardList>
      )}
      <EventTypeModal editing={editing} onClose={() => setEditing(null)} />
    </Card>
  );
}

interface TypeForm {
  title: string;
  slug: string;
  description: string;
  durationMin: string;
  bufferBeforeMin: string;
  bufferAfterMin: string;
  minNoticeHours: string;
  horizonDays: string;
  dailyCap: string;
  slotStepMin: string;
  listed: boolean;
  active: boolean;
  followup: boolean;
  questions: (Question & { optionsText: string })[];
}

const toForm = (t: EventType | null): TypeForm => ({
  title: t?.title ?? "",
  slug: t?.slug ?? "",
  description: t?.description ?? "",
  durationMin: String(t?.durationMin ?? 30),
  bufferBeforeMin: String(t?.bufferBeforeMin ?? 0),
  bufferAfterMin: String(t?.bufferAfterMin ?? 10),
  minNoticeHours: String((t?.minNoticeMin ?? 720) / 60),
  horizonDays: String(t?.horizonDays ?? 42),
  dailyCap: String(t?.dailyCap ?? 4),
  slotStepMin: String(t?.slotStepMin ?? 30),
  listed: t?.listed ?? true,
  active: t?.active ?? true,
  followup: t?.followup ?? true,
  questions: (t?.questions ?? []).map((q) => ({ ...q, optionsText: (q.options ?? []).join(", ") })),
});

const KINDS = [
  { value: "text", label: "Short answer" },
  { value: "textarea", label: "Long answer" },
  { value: "single", label: "Choose one" },
  { value: "multi", label: "Choose any" },
];

const TYPE_LABEL: Record<string, string> = {
  title: "Title",
  slug: "Address",
  description: "Description",
  durationMin: "Length",
  bufferBeforeMin: "Buffer before",
  bufferAfterMin: "Buffer after",
  minNoticeMin: "Notice",
  horizonDays: "Book up to",
  dailyCap: "Calls a day",
  slotStepMin: "Start times every",
  questions: "Questions",
};

function EventTypeModal({ editing, onClose }: { editing: EventType | "new" | null; onClose: () => void }) {
  const qc = useQueryClient();
  const current = editing === "new" ? null : editing;
  const [form, setForm] = useState<TypeForm>(() => toForm(null));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [issues, setIssues] = useState<FieldIssue[]>([]);
  const [saving, setSaving] = useState(false);
  const summary = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (editing) {
      setForm(toForm(current));
      setErrors({});
      setIssues([]);
    }
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = <K extends keyof TypeForm>(k: K, v: TypeForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const id = (k: string) => `et-${k.replace(/\./g, "-")}`;
  const show = (found: Record<string, string>) => {
    setErrors(found);
    setIssues(Object.entries(found).map(([k, message]) => ({ id: id(k === "minNoticeMin" ? "minNoticeHours" : k.startsWith("questions.") ? k.split(".").slice(0, 2).join(".") : k), label: TYPE_LABEL[k] ?? (k.startsWith("questions.") ? `Question ${Number(k.split(".")[1]) + 1}` : k), message })));
    requestAnimationFrame(() => summary.current?.focus());
  };
  const num = (s: string) => (s.trim() === "" ? Number.NaN : Number(s));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    const found: Record<string, string> = {};
    if (!form.title.trim()) found.title = "Enter a title";
    if (!/^[a-z0-9](?:[a-z0-9-]{0,58}[a-z0-9])?$/.test(form.slug.trim())) found.slug = "Use lower-case letters, digits and hyphens, like intro-call";
    for (const k of ["durationMin", "bufferBeforeMin", "bufferAfterMin", "horizonDays", "dailyCap", "slotStepMin"] as const) if (!Number.isFinite(num(form[k]))) found[k] = "Enter a number";
    if (!Number.isFinite(num(form.minNoticeHours))) found.minNoticeMin = "Enter a number of hours";
    form.questions.forEach((q, i) => {
      if (!q.label.trim()) found[`questions.${i}`] = "Enter the question";
      else if ((q.kind === "single" || q.kind === "multi") && q.optionsText.split(",").filter((o) => o.trim()).length < 2) found[`questions.${i}`] = "Give at least two options, separated by commas";
    });
    if (Object.keys(found).length) return show(found);
    const body = {
      title: form.title.trim(),
      slug: form.slug.trim(),
      description: form.description.trim() || null,
      durationMin: num(form.durationMin),
      bufferBeforeMin: num(form.bufferBeforeMin),
      bufferAfterMin: num(form.bufferAfterMin),
      minNoticeMin: Math.round(num(form.minNoticeHours) * 60),
      horizonDays: num(form.horizonDays),
      dailyCap: num(form.dailyCap),
      slotStepMin: num(form.slotStepMin),
      listed: form.listed,
      active: form.active,
      followup: form.followup,
      questions: form.questions.map((q, i) => ({
        key: q.key || questionKey(q.label, i),
        label: q.label.trim(),
        hint: q.hint?.trim() || null,
        kind: q.kind,
        options: q.kind === "single" || q.kind === "multi" ? q.optionsText.split(",").map((o) => o.trim()).filter(Boolean) : null,
        required: q.required,
      })),
    };
    setSaving(true);
    try {
      await api(current ? `/admin/book/event-types/${current.id}` : "/admin/book/event-types", { method: current ? "PATCH" : "POST", json: body });
      toaster.success({ title: current ? "Call type saved" : "Call type added" });
      await invalidateBook(qc);
      onClose();
    } catch (err) {
      const f = fieldErrors(err);
      if (Object.keys(f).length) show(Object.fromEntries(Object.entries(f).map(([k, v]) => [k.replace(/^(questions\.\d+)\..*$/, "$1"), v])));
      else notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  const numberField = (k: keyof TypeForm, label: string, hint?: string, errKey: string = k) => (
    <Field label={label} id={id(k)} hint={hint} error={errors[errKey]}>
      <Input type="number" inputMode="numeric" min={0} value={form[k] as string} onChange={(e) => set(k, e.target.value as never)} />
    </Field>
  );

  return (
    <Modal open={!!editing} onOpenChange={(o) => !o && onClose()} title={current ? `Edit ${current.title}` : "New call type"} description="Minutes are in steps of 5." wide>
      <form onSubmit={submit} noValidate className="@container space-y-4">
        {!!issues.length && <ErrorSummary ref={summary} issues={issues} className="" />}
        <div className="grid gap-3 @xl:grid-cols-2">
          <Field label="Title" id={id("title")} error={errors.title} required>
            <Input data-autofocus value={form.title} onChange={(e) => set("title", e.target.value)} maxLength={80} placeholder="Intro call" />
          </Field>
          <Field label="Address" id={id("slug")} hint="The end of the link: book.metrickle.com/intro." error={errors.slug} required>
            <Input value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase())} maxLength={60} placeholder="intro" />
          </Field>
        </div>
        <Field label="Description (optional)" id={id("description")} error={errors.description}>
          <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} maxLength={500} />
        </Field>
        <div className="grid gap-3 @xl:grid-cols-3">
          {numberField("durationMin", "Length (minutes)")}
          {numberField("bufferBeforeMin", "Buffer before (minutes)")}
          {numberField("bufferAfterMin", "Buffer after (minutes)")}
          {numberField("minNoticeHours", "Notice (hours)", "You can still book people in sooner.", "minNoticeMin")}
          {numberField("horizonDays", "Book up to (days ahead)")}
          {numberField("dailyCap", "Calls a day (most)")}
          {numberField("slotStepMin", "Start times every (minutes)")}
        </div>
        <div className="space-y-2">
          <Checkbox checked={form.active} onCheckedChange={(c) => set("active", c)} hint="Off: nobody can book it, and its link shows “Page not found”.">
            Bookable
          </Checkbox>
          <Checkbox checked={form.listed} onCheckedChange={(c) => set("listed", c)} hint="Off: only people with its link can book it.">
            Listed on the booking page
          </Checkbox>
          <Checkbox checked={form.followup} onCheckedChange={(c) => set("followup", c)} hint="90 minutes after the call, unless you mark it as a no-show. Off for calls like the scoping call.">
            Send the survey after the call
          </Checkbox>
        </div>
        <fieldset className="space-y-2">
          <legend className="text-[13px] font-medium text-ink">Questions on the booking form</legend>
          <p className="text-xs text-ink-3">At most 5. Keep them few: every question slows booking down.</p>
          <ListEditor
            items={form.questions}
            onChange={(questions) => set("questions", questions)}
            create={() => ({ key: "", label: "", hint: null, kind: "text" as const, options: null, required: false, optionsText: "" })}
            noun="question"
            label="Questions"
            keyOf={(_, i) => i}
            max={5}
            rowClassName="space-y-2 rounded-lg border border-line p-3"
            render={(q, i, controls) => {
              const upd = (patch: Partial<TypeForm["questions"][number]>) => set("questions", form.questions.map((x, j) => (j === i ? { ...x, ...patch } : x)));
              return (
                <div id={id(`questions.${i}`)} tabIndex={-1} className="space-y-2 outline-none">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <Field label={`Question ${i + 1}`} error={errors[`questions.${i}`]}>
                        <Input value={q.label} onChange={(e) => upd({ label: e.target.value })} maxLength={200} />
                      </Field>
                    </div>
                    <div className="pt-6">{controls}</div>
                  </div>
                  <div className="grid gap-2 @xl:grid-cols-2">
                    <Field label="Answer">
                      <SelectField className="w-full" items={KINDS} value={q.kind} onChange={(v) => upd({ kind: v as Question["kind"] })} />
                    </Field>
                    {(q.kind === "single" || q.kind === "multi") && (
                      <Field label="Options" hint="Separated by commas.">
                        <Input value={q.optionsText} onChange={(e) => upd({ optionsText: e.target.value })} />
                      </Field>
                    )}
                  </div>
                  <Checkbox checked={q.required} onCheckedChange={(c) => upd({ required: c })}>
                    Required
                  </Checkbox>
                </div>
              );
            }}
          />
        </fieldset>
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" pending={saving} pendingLabel="Saving…">
            {current ? "Save call type" : "Add call type"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** A question's key, from its label: "Company website" → "company_website". */
function questionKey(label: string, i: number): string {
  const k = label
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 36);
  return /^[a-z]/.test(k) ? k : `q${i + 1}_${k}`.slice(0, 40).replace(/_+$/, "");
}

// --- Weekly hours ----------------------------------------------------------------------------------

interface Window {
  weekday: number;
  start: string;
  end: string;
}

function HoursCard() {
  const q = useAvailability();
  const qc = useQueryClient();
  const [rows, setRows] = useState<Window[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [bad, setBad] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const toRows = (rules: WeeklyRule[]) => rules.map((r) => ({ weekday: r.weekday, start: minutesToTime(r.startMin), end: minutesToTime(r.endMin) }));
  useEffect(() => {
    if (q.data) setRows(toRows(q.data.rules));
  }, [q.data]);
  const dirty = q.data && JSON.stringify(rows) !== JSON.stringify(toRows(q.data.rules));

  const save = async () => {
    const wrong = new Set<number>();
    const rules = rows.map((r, i) => {
      const startMin = timeToMinutes(r.start);
      const endMin = timeToMinutes(r.end, true);
      if (!Number.isFinite(startMin) || !Number.isFinite(endMin) || startMin >= endMin || startMin % 5 || endMin % 5) wrong.add(i);
      return { weekday: r.weekday, startMin, endMin };
    });
    setBad(wrong);
    if (wrong.size) {
      setError(`Window ${[...wrong].map((i) => i + 1).join(", ")}: give a start before the end, on 5-minute steps.`);
      requestAnimationFrame(() => document.getElementById(`hours-${[...wrong][0]}-start`)?.focus());
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await api("/admin/book/availability/rules", { method: "PUT", json: { rules } });
      toaster.success({ title: "Weekly hours saved" });
      await invalidateBook(qc);
    } catch (err) {
      const f = fieldErrors(err);
      if (f.rules) setError(f.rules);
      else notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Weekly hours" subtitle={q.data?.timezone ? `When people can book, in your time (${q.data.timezone.replace(/_/g, " ")}).` : "When people can book, in your time."} />
      {q.isError ? (
        <BookError error={q.error} title="Couldn't load your hours" />
      ) : !q.data ? (
        <Skeleton className="m-4 h-32" />
      ) : (
        <CardSection className="space-y-3">
          <ListEditor
            items={rows}
            onChange={setRows}
            create={() => ({ ...(rows.at(-1) ?? { weekday: 1, start: "09:00", end: "17:00" }) })}
            noun="window"
            label="Weekly hours"
            addLabel="Add hours"
            keyOf={(_, i) => i}
            reorder={false}
            max={70}
            describe={(r) => `${WEEKDAYS[r.weekday - 1]} ${r.start} to ${r.end}`}
            rowClassName="flex flex-wrap items-center gap-2"
            render={(r, i, controls) => (
              <>
                <SelectField className="w-36" aria-label={`Window ${i + 1} day`} items={WEEKDAYS.map((d, n) => ({ value: String(n + 1), label: d }))} value={String(r.weekday)} onChange={(v) => setRows(rows.map((x, j) => (j === i ? { ...x, weekday: Number(v) } : x)))} />
                <Input id={`hours-${i}-start`} type="time" step={300} className="w-32" aria-label={`Window ${i + 1} starts`} aria-invalid={bad.has(i) || undefined} value={r.start} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} />
                <span className="text-sm text-ink-3" aria-hidden>
                  to
                </span>
                <Input type="time" step={300} className="w-32" aria-label={`Window ${i + 1} ends`} aria-invalid={bad.has(i) || undefined} value={r.end} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} />
                {controls}
              </>
            )}
          />
          {!rows.length && <p className="text-sm text-ink-3">No hours: nobody can book until you add some.</p>}
          {error && <FieldError role="alert">{error}</FieldError>}
          <div className="flex justify-end">
            <Button variant="primary" onClick={save} disabled={!dirty} pending={saving} pendingLabel="Saving…">
              Save hours
            </Button>
          </div>
        </CardSection>
      )}
    </Card>
  );
}

// --- Date overrides --------------------------------------------------------------------------------

function OverridesCard() {
  const q = useAvailability();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [form, setForm] = useState({ date: "", kind: "closed" as "closed" | "hours", start: "09:00", end: "13:00", note: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const addRef = useRef<HTMLButtonElement>(null);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    const found: Record<string, string> = {};
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) found.date = "Choose a date";
    const startMin = timeToMinutes(form.start);
    const endMin = timeToMinutes(form.end, true);
    if (form.kind === "hours" && !(startMin < endMin)) found.end = "End after the start";
    setErrors(found);
    if (Object.keys(found).length) {
      requestAnimationFrame(() => document.getElementById(found.date ? "ov-date" : "ov-end")?.focus());
      return;
    }
    setSaving(true);
    try {
      await api("/admin/book/availability/overrides", { method: "POST", json: { date: form.date, kind: form.kind, startMin: form.kind === "hours" ? startMin : null, endMin: form.kind === "hours" ? endMin : null, note: form.note.trim() || null } });
      toaster.success({ title: form.kind === "closed" ? "Day closed" : "Hours changed for that day" });
      setForm((f) => ({ ...f, date: "", note: "" }));
      await invalidateBook(qc);
    } catch (err) {
      const f = fieldErrors(err);
      if (Object.keys(f).length) setErrors(f);
      else notifyError(err);
    } finally {
      setSaving(false);
    }
  };
  const remove = async (o: Override) => {
    if (!(await confirm({ title: `Remove the change on ${o.date}?`, description: "Your usual weekly hours apply that day again.", confirmLabel: "Remove", focusAfter: () => addRef.current }))) return;
    try {
      await api(`/admin/book/availability/overrides/${o.id}`, { method: "DELETE" });
      toaster.success({ title: "Change removed" });
      await invalidateBook(qc);
    } catch (err) {
      notifyError(err);
    }
  };
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = (q.data?.overrides ?? []).filter((o) => o.date >= today);

  return (
    <Card>
      <CardHeader title="Days off and different hours" subtitle="Holidays, conferences, a short day. They replace your weekly hours on that date." />
      {q.isError ? (
        <BookError error={q.error} title="Couldn't load your days off" />
      ) : (
        <>
          <CardList loading={!q.data} empty="No changes coming up.">
            {upcoming.map((o) => (
              <CardRow key={o.id}>
                <span className="text-sm text-ink">
                  {new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${o.date}T00:00:00Z`))}
                  <span className="text-ink-2"> · {o.kind === "closed" ? "Closed" : `${minutesToTime(o.startMin!)} to ${minutesToTime(o.endMin!)}`}</span>
                  {o.note && <span className="text-ink-3"> · {o.note}</span>}
                </span>
                <IconButton label={`Remove the change on ${o.date}`} tone="danger" onClick={() => remove(o)}>
                  <Trash2 size={14} aria-hidden />
                </IconButton>
              </CardRow>
            ))}
          </CardList>
          <CardSection>
            <form onSubmit={add} noValidate className="@container space-y-3">
              <div className="grid gap-3 @xl:grid-cols-2">
                <Field label="Date" id="ov-date" error={errors.date}>
                  <Input type="date" min={today} value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
                </Field>
                <Field label="That day">
                  <SelectField
                    className="w-full"
                    items={[
                      { value: "closed", label: "Closed all day" },
                      { value: "hours", label: "Different hours" },
                    ]}
                    value={form.kind}
                    onChange={(v) => setForm((f) => ({ ...f, kind: v as "closed" | "hours" }))}
                  />
                </Field>
                {form.kind === "hours" && (
                  <>
                    <Field label="From" id="ov-start" error={errors.startMin}>
                      <Input type="time" step={300} value={form.start} onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))} />
                    </Field>
                    <Field label="To" id="ov-end" error={errors.end ?? errors.endMin}>
                      <Input type="time" step={300} value={form.end} onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))} />
                    </Field>
                  </>
                )}
              </div>
              <Field label="Note (optional)" hint="Only you see it.">
                <Input value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} maxLength={200} placeholder="Conference" />
              </Field>
              <div className="flex justify-end">
                <Button ref={addRef} type="submit" pending={saving} pendingLabel="Adding…">
                  Add change
                </Button>
              </div>
            </form>
          </CardSection>
        </>
      )}
      {dialog}
    </Card>
  );
}

// --- Your details ------------------------------------------------------------------------------------

function SettingsCard() {
  const q = useBookSettings();
  const qc = useQueryClient();
  const [form, setForm] = useState({ displayName: "", notifyEmail: "", timezone: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const s = q.data;
  useEffect(() => {
    if (s) setForm({ displayName: s.displayName ?? "", notifyEmail: s.notifyEmail ?? "", timezone: s.timezone ?? "" });
  }, [s]);
  const dirty = s && (form.displayName !== (s.displayName ?? "") || form.notifyEmail !== (s.notifyEmail ?? "") || form.timezone !== (s.timezone ?? ""));
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    const found: Record<string, string> = {};
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.notifyEmail.trim())) found.notifyEmail = "Enter an email address like name@example.com";
    setErrors(found);
    if (found.notifyEmail) return requestAnimationFrame(() => document.getElementById("set-email")?.focus());
    setSaving(true);
    try {
      await api("/admin/book/settings", { method: "PATCH", json: { displayName: form.displayName.trim() || null, notifyEmail: form.notifyEmail.trim(), timezone: form.timezone } });
      toaster.success({ title: "Your details saved" });
      await invalidateBook(qc);
    } catch (err) {
      const f = fieldErrors(err);
      if (Object.keys(f).length) setErrors(f);
      else notifyError(err);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card>
      <CardHeader title="Your details" subtitle="How the booking pages and emails name you, and where your emails go." />
      {q.isError ? (
        <BookError error={q.error} title="Couldn't load your details" />
      ) : !s ? (
        <Skeleton className="m-4 h-32" />
      ) : (
        <CardSection>
          <form onSubmit={save} noValidate className="@container space-y-3">
            <div className="grid gap-3 @xl:grid-cols-2">
              <Field label="Your name" id="set-name" hint={`“Book a call with ${form.displayName.trim() || s.brandName || "…"}”. Empty uses ${s.brandName ?? "the brand's name"}.`} error={errors.displayName}>
                <Input value={form.displayName} onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))} maxLength={60} />
              </Field>
              <Field label="Email for new bookings and alerts" id="set-email" error={errors.notifyEmail} required>
                <Input type="email" value={form.notifyEmail} onChange={(e) => setForm((f) => ({ ...f, notifyEmail: e.target.value }))} />
              </Field>
            </div>
            <Field label="Your time zone" id="set-tz" hint="Your weekly hours are in this zone. Calls already booked keep their times." error={errors.timezone}>
              <SelectField className="w-full" items={timeZoneOptions(form.timezone || null)} value={form.timezone || undefined} onChange={(v) => setForm((f) => ({ ...f, timezone: v }))} />
            </Field>
            <p className="text-xs text-ink-3">People's details are deleted {s.retentionDays} days after their call, or 30 days after it's cancelled.</p>
            <div className="flex justify-end">
              <Button type="submit" variant="primary" disabled={!dirty} pending={saving} pendingLabel="Saving…">
                Save details
              </Button>
            </div>
          </form>
        </CardSection>
      )}
    </Card>
  );
}
